import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConnectionError from "../../../components/ConnectionError/ConnectionError";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Modal } from "../../../components/common/Modal/Modal";
import { ConfirmationModal } from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { Pagination } from "../../../components/common/Pagination/Pagination";
import { useShowScrollTop } from "../../../hooks/useShowScrollTop";
import { adminService } from "../../../services/adminService";
import { getErrorMessage } from "../../../utils/errors";
import type { TeamMemberRecord, TeamMemberRequestData } from "../../../types";
import "./TeamPage.css";

interface TeamFormState {
  nameEn: string;
  nameAr: string;
  titleEn: string;
  titleAr: string;
  sortOrder: string;
}

const emptyForm: TeamFormState = {
  nameEn: "",
  nameAr: "",
  titleEn: "",
  titleAr: "",
  sortOrder: "0",
};

function TeamPage() {
  const { t } = useTranslation();
  const [members, setMembers] = useState<TeamMemberRecord[]>([]);

  // Pagination & Loading State
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Form Modal State (shared for create/edit)
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberRecord | null>(null);
  const [form, setForm] = useState<TeamFormState>(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<TeamMemberRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Floating Go to Top hook
  const { showScrollTop, scrollToTop } = useShowScrollTop(300);

  // Smart debounce: 700ms — enough time to finish typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 700);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load a specific page (replaces list content)
  const loadTeam = useCallback(
    async (pageToLoad: number) => {
      setIsFetching(true);
      setError(false);
      try {
        const response = await adminService.getTeam(debouncedSearch, pageToLoad, 10);
        setMembers(response.content);
        setPage(pageToLoad);
        setTotalPages(response.totalPages);
      } catch {
        setError(true);
      } finally {
        setIsFetching(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearch]
  );

  // Re-fetch from page 0 whenever the debounced search changes (incl. initial mount)
  useEffect(() => {
    loadTeam(0);
  }, [loadTeam]);

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  // Clean up object URL previews
  useEffect(() => {
    return () => {
      if (imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const openCreateModal = () => {
    setEditingMember(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview("");
    setFormModalOpen(true);
  };

  const openEditModal = (member: TeamMemberRecord) => {
    setEditingMember(member);
    setForm({
      nameEn: member.nameEn,
      nameAr: member.nameAr,
      titleEn: member.titleEn,
      titleAr: member.titleAr,
      sortOrder: String(member.sortOrder),
    });
    setImageFile(null);
    setImagePreview(member.imageUrl || "");
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModalOpen(false);
    setEditingMember(null);
    setImageFile(null);
    setImagePreview("");
  };

  const handleFieldChange =
    (field: keyof TeamFormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    if (imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(file ? URL.createObjectURL(file) : editingMember?.imageUrl || "");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      let imageUrl = editingMember?.imageUrl;
      if (imageFile) {
        const uploaded = await adminService.uploadImage(imageFile);
        imageUrl = uploaded.url;
      }

      const data: TeamMemberRequestData = {
        nameEn: form.nameEn.trim(),
        nameAr: form.nameAr.trim(),
        titleEn: form.titleEn.trim(),
        titleAr: form.titleAr.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (imageUrl) data.imageUrl = imageUrl;

      if (editingMember) {
        await adminService.updateTeamMember(editingMember.id, data);
        toast.success(t("admin.memberUpdated"));
      } else {
        await adminService.createTeamMember(data);
        toast.success(t("admin.memberCreated"));
      }
      setFormModalOpen(false);
      setEditingMember(null);
      setImageFile(null);
      setImagePreview("");
      loadTeam(page);
    } catch (submitError) {
      toast.error(await getErrorMessage(submitError, t("admin.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (member: TeamMemberRecord) => {
    setMemberToDelete(member);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteTeamMember(memberToDelete.id);
      toast.success(t("admin.memberDeleted"));
      setDeleteModalOpen(false);
      setMemberToDelete(null);
      // If we just removed the last card on a page beyond the first, step back one page
      loadTeam(members.length === 1 && page > 0 ? page - 1 : page);
    } catch (deleteError) {
      toast.error(await getErrorMessage(deleteError, t("admin.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  // Only truthy when the user has typed something that hasn't been debounced yet.
  const isSearching = searchTerm !== debouncedSearch;

  // Only block the whole page on the true initial mount.
  if (initialLoading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={() => loadTeam(page)} />;
  }

  return (
    <div className="admin-team-page">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t("admin.team")}</h1>
          <p className="admin-page-subtitle">{t("admin.teamDesc")}</p>
        </div>
        <button type="button" className="tm-add-btn" onClick={openCreateModal}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("admin.addMember")}
        </button>
      </div>

      {/* Leadership hint */}
      <p className="tm-hint">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        {t("admin.teamHint")}
      </p>

      {/* ─── Search Toolbar ─── */}
      <div className="tm-toolbar-wrap">
        <div className="tm-toolbar">
          <div className="tm-search-field">
            <span className="tm-search-field__icon">
              {isSearching ? (
                <span className="tm-search-spinner" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="17"
                  height="17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </span>
            <input
              type="text"
              className="tm-search-field__input"
              placeholder={t("admin.searchTeam", "Search by name or title...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                className="tm-search-field__clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {/* Thin progress bar — visible only during silent re-fetches */}
        <div
          className={`tm-fetch-bar ${isFetching ? "tm-fetch-bar--active" : ""}`}
        />
      </div>

      {/* Team Member Cards */}
      {isFetching && members.length === 0 ? (
        <div className="admin-loading">
          <span className="auth-spinner auth-spinner-brand"></span>
        </div>
      ) : members.length === 0 ? (
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
          title={debouncedSearch ? t("admin.noSearchResults", "No results found") : t("admin.noMembers")}
          description={debouncedSearch ? t("admin.noSearchResultsDesc", "Try a different name or title.") : t("admin.noMembersDesc")}
        />
      ) : (
        <div className="tm-list">
          {members.map((member) => (
            <article key={member.id} className="tm-card">
              <div className="tm-card__thumb">
                {member.imageUrl ? (
                  <img src={member.imageUrl} alt={member.nameEn} />
                ) : (
                  <div className="tm-card__thumb-placeholder">
                    {member.nameEn.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              <div className="tm-card__info">
                <div className="tm-card__name-row">
                  <span className="tm-card__name">{member.nameEn}</span>
                  <span className="tm-card__name-ar">{member.nameAr}</span>
                </div>
                <div className="tm-card__meta">
                  <span className="tm-meta-chip">{member.titleEn}</span>
                  <span className="tm-meta-chip">{member.titleAr}</span>
                  <span className="tm-meta-chip">#{member.sortOrder}</span>
                </div>
              </div>

              <div className="tm-card__actions">
                <button
                  type="button"
                  className="tm-action-btn tm-action-btn--edit"
                  onClick={() => openEditModal(member)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  {t("admin.edit")}
                </button>
                <button
                  type="button"
                  className="tm-action-btn tm-action-btn--delete"
                  onClick={() => handleDeleteClick(member)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  {t("admin.delete")}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Numbered pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={loadTeam}
        disabled={isFetching}
      />

      {/* Create / Edit Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        title={editingMember ? t("admin.editMember") : t("admin.addMember")}
        className="tm-form-modal"
      >
        <form className="tm-form" onSubmit={handleSubmit}>
          <div className="tm-form__row">
            <div className="tm-form__field">
              <label className="tm-form__label">{t("admin.nameEn")}</label>
              <input
                type="text"
                className="tm-form__input"
                value={form.nameEn}
                onChange={handleFieldChange("nameEn")}
                required
                maxLength={120}
              />
            </div>
            <div className="tm-form__field">
              <label className="tm-form__label">{t("admin.nameAr")}</label>
              <input
                type="text"
                className="tm-form__input"
                dir="rtl"
                value={form.nameAr}
                onChange={handleFieldChange("nameAr")}
                required
                maxLength={120}
              />
            </div>
          </div>

          <div className="tm-form__row">
            <div className="tm-form__field">
              <label className="tm-form__label">{t("admin.jobTitleEn")}</label>
              <input
                type="text"
                className="tm-form__input"
                value={form.titleEn}
                onChange={handleFieldChange("titleEn")}
                required
                maxLength={160}
              />
            </div>
            <div className="tm-form__field">
              <label className="tm-form__label">{t("admin.jobTitleAr")}</label>
              <input
                type="text"
                className="tm-form__input"
                dir="rtl"
                value={form.titleAr}
                onChange={handleFieldChange("titleAr")}
                required
                maxLength={160}
              />
            </div>
          </div>

          <div className="tm-form__row">
            <div className="tm-form__field">
              <label className="tm-form__label">{t("admin.sortOrder")}</label>
              <input
                type="number"
                className="tm-form__input"
                min="1"
                value={form.sortOrder}
                onChange={handleFieldChange("sortOrder")}
                required
              />
            </div>
          </div>

          <div className="tm-form__field">
            <label className="tm-form__label">{t("admin.image")}</label>
            <div className="tm-image-upload">
              {imagePreview && (
                <div className="tm-image-upload__preview">
                  <img src={imagePreview} alt={t("admin.currentImage")} />
                </div>
              )}
              <label className="tm-image-upload__picker">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t("admin.chooseImage")}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  hidden
                />
              </label>
            </div>
          </div>

          <div className="tm-form__actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={closeFormModal}
              disabled={saving}
            >
              {t("admin.cancel")}
            </button>
            <button type="submit" className="btn btn-filled" disabled={saving}>
              {saving ? (
                <span className="tm-btn-loading">
                  <span className="auth-spinner auth-spinner-white" />
                </span>
              ) : (
                t("admin.save")
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t("admin.deleteMemberConfirmTitle")}
        message={t("admin.deleteMemberConfirmDesc")}
        confirmLabel={t("admin.delete") || "Delete"}
        cancelLabel={t("admin.cancel") || "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        type="danger"
      />

      {/* Floating scroll-to-top */}
      <button
        className={`admin-go-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Go to top"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default TeamPage;
